import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Star, MapPin } from "lucide-react";
import { BUSINESS, IMAGES } from "@/data/content";
import { useOpenStatus } from "@/components/site/useOpenStatus";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Line = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      variants={lineReveal}
      custom={i}
      initial="hidden"
      animate="show"
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const status = useOpenStatus();

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={IMAGES.heroImage}
          alt="Il fornaio di Laurie che versa la crema nel laboratorio"
          className="w-full h-full object-cover scale-[1.05] origin-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#2A1B14]/55 via-[#2A1B14]/25 to-[#2A1B14]/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2A1B14]/75 via-[#2A1B14]/20 to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 pt-[135px] sm:pt-[160px] pb-28 lg:pb-16 min-h-[100svh] flex flex-col justify-between"
      >
        <div />

        <div className="max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.7 }}
            className="flex flex-wrap items-center gap-3 mb-6"
          >
            <a
              href={BUSINESS.reviewsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#FDFBF7]/90 backdrop-blur px-3.5 py-1.5 text-[#2A1B14] hover:bg-[#FDFBF7] transition-colors duration-300"
              data-testid="hero-reviews-link"
            >
              <Star size={14} className="fill-[#D4AF37] text-[#D4AF37]" />
              <span className="font-body text-xs font-semibold">
                {BUSINESS.rating} · {BUSINESS.reviewsCount} recensioni Google
              </span>
            </a>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FDFBF7]/90 backdrop-blur px-3.5 py-1.5 text-[#2A1B14]">
              <MapPin size={14} />
              <span className="font-body text-xs font-medium">Chiarano (TV)</span>
            </span>
          </motion.div>

          <h1 className="font-display text-[#FDFBF7] tracking-tight leading-[0.92] text-[clamp(3rem,10vw,9rem)] font-light">
            <Line i={0}>Il</Line>
            <Line i={1}>
              <span className="italic font-normal">laboratorio</span>
            </Line>
            <Line i={2}>di Laurie</Line>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-7 max-w-md font-body text-[#FDFBF7]/90 text-base sm:text-lg leading-relaxed"
          >
            Pane, pasticceria e caffetteria fatti freschi ogni giorno nel nostro
            laboratorio. Ingredienti selezionati, mani esperte, idee originali.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-9 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4"
          >
            <a
              href="#menu"
              className="group inline-flex w-full sm:w-auto justify-center items-center gap-2 rounded-full bg-[#FDFBF7] text-[#2A1B14] px-7 py-4 font-body font-semibold text-sm hover:bg-[#D4AF37] transition-all duration-400 hover:scale-[0.98]"
              data-testid="hero-menu-btn"
            >
              Scopri il menu
              <ArrowDown size={17} className="group-hover:translate-y-0.5 transition-transform" />
            </a>
            <a
              href="#dove-siamo"
              className="inline-flex w-full sm:w-auto justify-center items-center gap-2 rounded-full border border-[#FDFBF7]/60 text-[#FDFBF7] px-7 py-4 font-body font-medium text-sm hover:bg-[#FDFBF7]/10 transition-all duration-400"
              data-testid="hero-visit-btn"
            >
              Vieni a trovarci
            </a>
            <span
              className="inline-flex items-center gap-2 font-body text-sm text-[#FDFBF7]/90"
              data-testid="hero-status"
            >
              <span className="relative flex h-2.5 w-2.5">
                {status.open && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#8C9B86] opacity-75" />
                )}
                <span
                  className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                    status.open ? "bg-[#8C9B86]" : "bg-[#D4AF37]"
                  }`}
                />
              </span>
              {status.label}
            </span>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
